import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, BookOpenIcon } from 'lucide-react';
import { books, Book } from '../data/books';
import { SectionHeading } from './SectionHeading';
export function Catalog() {
  const [selected, setSelected] = useState<Book | null>(null);
  return <section id="catalog" className="w-full bg-paper py-28" data-id="element-34" data-mp="1fsm1z53hlghu">
      <div className="mx-auto max-w-6xl px-6" data-id="element-35" data-mp="1159e39q7iyoa">
        <SectionHeading eyebrow="Our Catalog" title="Three books in print" description="Each title is a considered work — from ancient beauty to the mysteries of the soul." data-id="element-36" data-mp="113g5vpzy46e6" />

        <div className="mt-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-37" data-mp="s65snz3ki76m">
          {books.map((book, i) => <motion.article key={book.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} className="group flex flex-col" data-id="element-38" data-mp="x11mn4sd74md" data-mpi={i}>
              <button onClick={() => setSelected(book)} className="relative block overflow-hidden bg-ink/5 shadow-lg shadow-black/10" aria-label={`View details for ${book.title}`} data-id="element-39" data-mp="20hpfvxs94tv4">
                <img src={book.cover} alt={`${book.title} cover`} className="w-full aspect-[2/3] object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-40" data-mp="2afvkin5g0e3o" />
                <span className="absolute inset-0 flex items-center justify-center bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-41" data-mp="1vi36jzfjnxrl">
                  <span className="flex items-center gap-2 text-paper text-sm tracking-wide" data-id="element-42" data-mp="1qql57a9lzv63">
                    <BookOpenIcon size={16} data-id="element-43" data-mp="130h9rq6kr1q6" /> View details
                  </span>
                </span>
              </button>

              <div className="mt-6 flex-1" data-id="element-44" data-mp="yy47jbr1dly5">
                <p className="text-gold text-xs uppercase tracking-[0.3em] mb-2" data-id="element-45" data-mp="2y5hz96kw2o4p">
                  {book.genre}
                </p>
                <h3 className="font-serif text-2xl text-ink leading-tight" data-id="element-46" data-mp="332gtdp4jyv2w">
                  {book.title}
                </h3>
                <p className="text-ink/55 text-sm mt-1" data-id="element-47" data-mp="37x4a31tbel6x">
                  by {book.author} · {book.year}
                </p>
              </div>
            </motion.article>)}
        </div>
      </div>

      <AnimatePresence data-id="element-48" data-mp="2k57ac9353jm6">
        {selected && <BookModal book={selected} onClose={() => setSelected(null)} data-id="element-49" data-mp="2k5yvsr30w0yq" />}
      </AnimatePresence>
    </section>;
}
function BookModal({
  book,
  onClose
}: {
  book: Book;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} onClick={onClose} className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-label={`${book.title} details`} data-id="element-50" data-mp="2u4y2i4489mj3">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3
    }} onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl bg-paper max-h-[90vh] overflow-y-auto shadow-2xl" data-id="element-51" data-mp="1twzqg7f0pag6">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-ink/60 hover:text-ink bg-paper/80 rounded-full p-1" aria-label="Close" data-id="element-52" data-mp="1a7z2alfyt4pv">
          <XIcon size={22} data-id="element-53" data-mp="117kffhncrhk9" />
        </button>

        <div className="grid sm:grid-cols-[240px_1fr]" data-id="element-54" data-mp="118cckwwllncn">
          <div className="bg-ink p-8 flex items-center justify-center" data-id="element-55" data-mp="w8pzcngrs73u">
            <img src={book.cover} alt={`${book.title} cover`} className="w-44 shadow-xl shadow-black/50" data-id="element-56" data-mp="2vl25bbb8cqig" />
          </div>
          <div className="p-8 sm:p-10" data-id="element-57" data-mp="1o81gmhz6t9ys">
            <p className="text-gold text-xs uppercase tracking-[0.3em] mb-3" data-id="element-58" data-mp="1o92usvbi2ckq">
              {book.genre}
            </p>
            <h3 className="font-serif text-3xl text-ink leading-tight" data-id="element-59" data-mp="1s938ueuoslr9">
              {book.title}
            </h3>
            <p className="font-serif italic text-ink/60 text-lg mt-1" data-id="element-60" data-mp="1eby5immcy0zp">
              {book.subtitle}
            </p>
            <p className="text-ink/55 text-sm mt-3" data-id="element-61" data-mp="14k25v74mi2td">by {book.author}</p>

            <p className="text-ink/75 leading-relaxed mt-6" data-id="element-62" data-mp="14ir39amwz13u">{book.description}</p>

            <dl className="mt-8 grid grid-cols-2 gap-y-3 text-sm border-t border-ink/10 pt-6" data-id="element-63" data-mp="19bk43nf9hpan">
              <dt className="text-ink/50" data-id="element-64" data-mp="vkndu38t5k6r">Format</dt>
              <dd className="text-ink text-right" data-id="element-65" data-mp="10i75q5yfhvh6">{book.format}</dd>
              <dt className="text-ink/50" data-id="element-66" data-mp="10ip1z2l3c9p3">Pages</dt>
              <dd className="text-ink text-right" data-id="element-67" data-mp="lkwjq2o5t777">{book.pages}</dd>
              <dt className="text-ink/50" data-id="element-68" data-mp="qk9inlsb24q5">Published</dt>
              <dd className="text-ink text-right" data-id="element-69" data-mp="qiy5ivcyzkmd">{book.year}</dd>
              <dt className="text-ink/50" data-id="element-70" data-mp="6xxwyl08z0yt">ISBN</dt>
              <dd className="text-ink text-right" data-id="element-71" data-mp="6yr59qeutyj2">{book.isbn}</dd>
            </dl>

            <a href={book.purchaseUrl ?? '#contact'} onClick={book.purchaseUrl ? undefined : onClose} target={book.purchaseUrl ? '_blank' : undefined} rel={book.purchaseUrl ? 'noreferrer' : undefined} className="inline-block mt-8 px-7 py-3 bg-ink text-paper text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors" data-id="element-72" data-mp="2ygorjtfrppy">
              {book.purchaseUrl ? 'Buy on Amazon' : 'Inquire about this title'}
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}